import { useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Shuffle } from "lucide-react";
import { useCreateCoupon } from "@/hooks/useCoupons";

interface AddCouponDialogProps {
  providerId: string;
  services: Array<{ id: string; name: string; }>;
  onCouponAdded: () => void;
}

interface CouponFormData {
  code: string;
  name: string;
  description?: string;
  discount_type: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits';
  discount_value?: number;
  credits_per_period?: number;
  max_discount_amount?: number;
  min_order_amount?: number;
  usage_limit?: number;
  valid_from?: string;
  valid_until?: string;
  service_ids: string[];
}

const AddCouponDialog = ({ providerId, services, onCouponAdded }: AddCouponDialogProps) => {
  const [open, setOpen] = useState(false);
  const createCouponMutation = useCreateCoupon();

  const form = useForm<CouponFormData>({
    defaultValues: {
      code: '',
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_value: undefined,
      credits_per_period: undefined,
      max_discount_amount: undefined,
      min_order_amount: undefined,
      usage_limit: undefined,
      valid_from: '',
      valid_until: '',
      service_ids: [],
    },
  });

  const discountType = form.watch('discount_type');
  const isCreditBased = discountType === 'weekly_credits' || discountType === 'monthly_credits';

  const generateRandomCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 7; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    form.setValue('code', result);
  };

  const onSubmit = async (data: CouponFormData) => {
    try {
      console.log('Form data before processing:', data);
      
      // Helper function to convert empty strings to undefined for numeric fields
      const parseNumericField = (value: any): number | undefined => {
        if (value === '' || value === null || value === undefined || value === 0) {
          return undefined;
        }
        const parsed = Number(value);
        return isNaN(parsed) ? undefined : parsed;
      };

      // Validate required fields based on coupon type
      if (isCreditBased) {
        if (!data.credits_per_period || Number(data.credits_per_period) <= 0) {
          throw new Error('Credits per period is required for credit-based coupons');
        }
      } else {
        if (!data.discount_value || Number(data.discount_value) <= 0) {
          throw new Error('Discount value is required for non-credit based coupons');
        }
      }

      const couponData = {
        code: data.code.toUpperCase(),
        name: data.name,
        description: data.description || undefined,
        discount_type: data.discount_type,
        discount_value: isCreditBased ? undefined : parseNumericField(data.discount_value),
        credits_per_period: isCreditBased ? parseNumericField(data.credits_per_period) : undefined,
        max_discount_amount: !isCreditBased ? parseNumericField(data.max_discount_amount) : undefined,
        min_order_amount: !isCreditBased ? parseNumericField(data.min_order_amount) : undefined,
        usage_limit: parseNumericField(data.usage_limit),
        valid_from: data.valid_from || new Date().toISOString(),
        valid_until: data.valid_until && data.valid_until.trim() !== '' ? data.valid_until : undefined,
        service_ids: data.service_ids
      };

      console.log('Processed coupon data:', couponData);

      await createCouponMutation.mutateAsync({
        providerId,
        couponData
      });

      setOpen(false);
      form.reset();
      onCouponAdded();
    } catch (error) {
      console.error('Error creating coupon:', error);
    }
  };

  const handleServiceToggle = (serviceId: string, checked: boolean) => {
    const currentServices = form.getValues('service_ids');
    if (checked) {
      form.setValue('service_ids', [...currentServices, serviceId]);
    } else {
      form.setValue('service_ids', currentServices.filter(id => id !== serviceId));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Coupon
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Coupon</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="coupon-code">Coupon Code *</Label>
              <div className="flex gap-2">
                <Input
                  id="coupon-code"
                  {...form.register('code', { required: true })}
                  placeholder="e.g., WELCOME20"
                  className="uppercase flex-1"
                  onChange={(e) => form.setValue('code', e.target.value.toUpperCase())}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={generateRandomCode}
                  title="Generate random code"
                >
                  <Shuffle className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Will be automatically converted to uppercase
              </p>
            </div>

            <div>
              <Label htmlFor="coupon-name">Coupon Name *</Label>
              <Input
                id="coupon-name"
                {...form.register('name', { required: true })}
                placeholder="e.g., Welcome Discount"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="coupon-description">Description</Label>
            <Textarea
              id="coupon-description"
              {...form.register('description')}
              placeholder="Enter coupon description (optional)"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Discount Type *</Label>
              <Select
                value={form.watch('discount_type')}
                onValueChange={(value: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits') => 
                  form.setValue('discount_type', value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed Amount (£)</SelectItem>
                  <SelectItem value="weekly_credits">Weekly Credits</SelectItem>
                  <SelectItem value="monthly_credits">Monthly Credits</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!isCreditBased && (
              <div>
                <Label htmlFor="discount-value">
                  Discount Value * {form.watch('discount_type') === 'percentage' ? '(%)' : '(£)'}
                </Label>
                <Input
                  id="discount-value"
                  type="number"
                  step={form.watch('discount_type') === 'percentage' ? '1' : '0.01'}
                  min="0"
                  max={form.watch('discount_type') === 'percentage' ? '100' : undefined}
                  {...form.register('discount_value', { 
                    required: !isCreditBased, 
                    min: 0,
                    max: form.watch('discount_type') === 'percentage' ? 100 : undefined
                  })}
                  placeholder={form.watch('discount_type') === 'percentage' ? 'e.g., 20' : 'e.g., 10.00'}
                />
              </div>
            )}

            {isCreditBased && (
              <div>
                <Label htmlFor="credits-per-period">
                  Credits Per {discountType === 'weekly_credits' ? 'Week' : 'Month'} *
                </Label>
                <Input
                  id="credits-per-period"
                  type="number"
                  min="1"
                  {...form.register('credits_per_period', { 
                    required: isCreditBased, 
                    min: 1
                  })}
                  placeholder="e.g., 3"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Number of free sessions per {discountType === 'weekly_credits' ? 'week' : 'month'}
                </p>
              </div>
            )}
          </div>

          {!isCreditBased && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="max-discount">Maximum Discount Amount (£)</Label>
                <Input
                  id="max-discount"
                  type="number"
                  step="0.01"
                  min="0"
                  {...form.register('max_discount_amount')}
                  placeholder="Optional - e.g., 50.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  For percentage discounts, cap the maximum discount amount
                </p>
              </div>

              <div>
                <Label htmlFor="min-order">Minimum Order Amount (£)</Label>
                <Input
                  id="min-order"
                  type="number"
                  step="0.01"
                  min="0"
                  {...form.register('min_order_amount')}
                  placeholder="Optional - e.g., 25.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum order value required to use this coupon
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="usage-limit">Usage Limit</Label>
              <Input
                id="usage-limit"
                type="number"
                min="1"
                {...form.register('usage_limit')}
                placeholder="Optional - e.g., 100"
              />
              <p className="text-xs text-gray-500 mt-1">
                {isCreditBased 
                  ? 'Maximum number of users who can claim this coupon'
                  : 'Maximum number of times this coupon can be used'
                }
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="valid-from">Valid From</Label>
              <Input
                id="valid-from"
                type="date"
                {...form.register('valid_from')}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <Label htmlFor="valid-until">Valid Until</Label>
              <Input
                id="valid-until"
                type="date"
                {...form.register('valid_until')}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty for no expiry date
              </p>
            </div>
          </div>

          <div>
            <Label>Applicable Services *</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 p-4 border rounded-lg max-h-40 overflow-y-auto">
              {services.map((service) => (
                <div key={service.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`service-${service.id}`}
                    checked={form.watch('service_ids').includes(service.id)}
                    onCheckedChange={(checked) => handleServiceToggle(service.id, checked as boolean)}
                  />
                  <Label htmlFor={`service-${service.id}`} className="text-sm">
                    {service.name}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Select which services this coupon applies to
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={createCouponMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createCouponMutation.isPending || form.watch('service_ids').length === 0}
            >
              {createCouponMutation.isPending ? 'Creating...' : 'Create Coupon'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCouponDialog;
